import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import type { DateRange } from 'react-day-picker';
import { isFuture, format, parse, endOfDay } from 'date-fns';
import { CalendarIcon, TagIcon, ClockIcon } from 'lucide-react';
import {
  formatPhoneNumber,
  type CallsForHistory,
  type CallsForHistoryQueryParams,
} from '@intone/shared';

import { DataTable, type Column, type FilterConfig } from '@/shared/ui/DataTable';
import { formatDuration, formatDateHistory } from '@/shared/lib/date';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

import { useCallsForHistory } from '@/entities/call';
import { useCampaignAvailableDispositions } from '@/entities/campaign';
import { DispositionBadge } from '@/features/dispositionBadge';
import { CallDetails } from '@/widgets/callDetails';

import {
  useHashParams,
  validateParams,
  dateFormatStr,
} from '../model/useHashParams';

interface VoiceHistoryContentProps {
  campaignId: number
}

export function VoiceHistoryContent({ campaignId }: VoiceHistoryContentProps) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { callId } = useParams<{ callId?: string }>();

  const [hashParams, setHashParams] = useHashParams();
  const initFromHash = useRef(validateParams(hashParams));
  const dateRangeDerived = useRef(false);

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [sortField, setSortField] = useState<string>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [durationFilter, setDurationFilter] = useState<string>('0');
  const [dispositionFilter, setDispositionFilter] = useState<string>('all');
  const [autoQAFilter, setAutoQAFilter] = useState<string>('all');
  const [phoneFilter, setPhoneFilter] = useState<string>('');

  useEffect(() => {
    if (validateParams(hashParams)) {
      setDateRange({
        from: parse(hashParams.start, dateFormatStr, new Date()),
        to: parse(hashParams.end, dateFormatStr, new Date()),
      });
      dateRangeDerived.current = true;
    }
  }, [hashParams.start, hashParams.end]);

  useEffect(() => {
    if (!initFromHash.current) {
      dateRangeDerived.current = false;
      setDateRange(undefined);
    }
    initFromHash.current = false;
  }, [campaignId]);

  const queryParams: CallsForHistoryQueryParams = useMemo(
    () => {
      const base: CallsForHistoryQueryParams = {
        pageSize,
        pageId: currentPage,
        orderBy: sortField as CallsForHistoryQueryParams['orderBy'],
        orderType: sortDirection,
        ...(durationFilter !== '0' && { minDurationSeconds: durationFilter }),
        ...(dispositionFilter !== 'all' && { disposition: dispositionFilter }),
        ...(autoQAFilter !== 'all' && { autoQaYesMin: autoQAFilter }),
        ...(phoneFilter && { phone: phoneFilter }),
      };

      if (dateRangeDerived.current && hashParams.start && hashParams.end && validateParams(hashParams)) {
        const from = parse(hashParams.start, dateFormatStr, new Date());
        const to = parse(hashParams.end, dateFormatStr, new Date());
        base.startDate = from.toISOString();
        base.endDate = endOfDay(to).toISOString();
      }

      return base;
    },
    [
      hashParams.start, hashParams.end,
      currentPage, pageSize, sortField, sortDirection,
      durationFilter, dispositionFilter, autoQAFilter, phoneFilter,
      hash, campaignId
    ]
  );

  const { data, isLoading, isFetching, error } = useCallsForHistory(campaignId, queryParams);
  const calls: CallsForHistory[] = data?.calls || [];
  const totalCount = data?.totalCount || 0;
  const initialLoading = isLoading && !data;

  useEffect(() => {
    if (!dateRangeDerived.current && calls.length > 0) {
      const timestamps = calls
        .map(c => c.timestamp ? new Date(c.timestamp).getTime() : 0)
        .filter(t => t > 0);
      if (timestamps.length > 0) {
        const minDate = new Date(Math.min(...timestamps));
        const maxDate = new Date(Math.max(...timestamps));
        setDateRange({ from: minDate, to: maxDate });
        dateRangeDerived.current = true;
      }
    }
  }, [calls]);

  const { data: availableDispositionsCodes } = useCampaignAvailableDispositions(campaignId);

  const columns: Column<CallsForHistory>[] = useMemo(() => [
    {
      header: 'Number',
      accessorKey: 'phone',
      sortField: 'phone',
      sortable: true,
      cell: (value: string | null) => formatPhoneNumber(value),
      width: { min: 170, max: 170 }
    },
    {
      header: 'Start time',
      accessorKey: 'timestamp',
      sortField: 'timestamp',
      sortable: true,
      cell: (value: Date | null) => {
        if (!value) return '-'
        return formatDateHistory(value, 'MMM d, yyyy h:mm a')
      },
      width: { min: 180, max: 180 }
    },
    {
      header: <div className="flex items-center justify-center"><TagIcon size={16} /><span className="sr-only">Disposition</span></div>,
      accessorKey: 'disposition',
      sortField: 'disposition',
      sortable: true,
      cell: (value: string | null) => (
        <DispositionBadge
          disposition={value || '-'}
          dispositions={[]}
          isEditable={false}
        />
      ),
      width: { min: 90, max: 90 }
    },
    {
      header: <div className="flex items-center justify-center"><ClockIcon size={16} /><span className="sr-only">Duration</span></div>,
      accessorKey: 'duration',
      sortField: 'duration',
      sortable: true,
      cell: (value: number | null) => formatDuration(value),
      width: { min: 75, max: 75 }
    },
  ], []);

  const filters: FilterConfig[] = useMemo(() => {
    const baseFilters: FilterConfig[] = [
      { type: 'text', key: 'phone', label: 'Phone Number', placeholder: 'Search by phone...' },
      {
        type: 'select', key: 'duration', label: 'Min Duration',
        options: [
          { value: '0', label: 'All calls' },
          { value: '-30', label: 'Under 30 seconds' },
          { value: '30', label: '30+ seconds' },
          { value: '60', label: '1+ minute' },
          { value: '120', label: '2+ minutes' },
        ],
        noFilterValue: '0',
      },
      {
        type: 'select', key: 'auto_qa_yes_min', label: 'Automated QA',
        options: [
          { value: 'all', label: 'All' },
          { value: '1', label: '1 flag or more' },
          { value: '2', label: '2 flags or more' },
          { value: '3', label: '3 flags or more' },
        ],
        noFilterValue: 'all',
      },
    ]
    if (availableDispositionsCodes && availableDispositionsCodes.length > 0) {
      baseFilters.push({
        type: 'select', key: 'disposition', label: 'Disposition',
        options: [
          { value: 'all', label: 'All' },
          ...availableDispositionsCodes.map(d => ({ value: d, label: d }))
        ],
        noFilterValue: 'all',
      })
    }
    return baseFilters
  }, [availableDispositionsCodes]);

  const filterValues = useMemo(
    () => ({ phone: phoneFilter, duration: durationFilter, auto_qa_yes_min: autoQAFilter, disposition: dispositionFilter }),
    [phoneFilter, durationFilter, dispositionFilter, autoQAFilter]
  );

  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'phone': setPhoneFilter(value); break
      case 'duration': setDurationFilter(value); break
      case 'disposition': setDispositionFilter(value); break
      case 'auto_qa_yes_min': setAutoQAFilter(value); break
    }
  }

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open && dateRange?.from && dateRange?.to) {
        setHashParams({ start: format(dateRange.from, dateFormatStr), end: format(dateRange.to, dateFormatStr) });
      }
    },
    [dateRange, setHashParams]
  );

  const handleRowClick = (row: CallsForHistory) => {
    navigate(`/history/${row.id}${hash}`);
  }

  return (
    <>
      <label className="flex items-center gap-2 w-max mb-4">
        <span className="text-body font-medium whitespace-nowrap">Date Range:</span>
        <Popover onOpenChange={handleClose}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[260px] justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span>
                {!dateRange ? 'Select Date Range:' : (
                  <>
                    {dateRange.from && formatDateHistory(dateRange.from, 'LLL dd, y')}{dateRange.to ? ` - ${formatDateHistory(dateRange.to, 'LLL dd, y')}` : undefined}
                  </>
                )}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={dateRange?.to}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              disabled={(date) => isFuture(date)}
            />
          </PopoverContent>
        </Popover>
      </label>

      <div className="mt-4 overflow-hidden flex flex-col lg:flex-row gap-4">
        <div className={`flex-1 overflow-hidden ${isFetching && !initialLoading ? 'opacity-60 transition-opacity duration-200' : 'transition-opacity duration-200'}`}>
          <div className="overflow-x-auto">
            <DataTable<CallsForHistory>
              columns={columns}
              data={calls}
              loading={initialLoading}
              error={error?.message}
              pagination={{ currentPage, pageSize, totalCount }}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              sorting={{ sortField, sortDirection }}
              onSortChange={(sf, sd) => { setSortField(sf); setSortDirection(sd) }}
              filters={filters}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onRowClick={handleRowClick}
              selectedId={callId}
              showDownload={true}
              downloadFileName={`call-history-${campaignId}-${formatDateHistory(dateRange?.from || new Date(), 'yyyy-MM-dd')}.csv`}
            />
          </div>
        </div>

        <div className="lg:flex-1 lg:min-w-[400px] lg:max-w-[600px]">
          <CallDetails callId={callId || null} />
        </div>
      </div>
    </>
  )
}
